<template>
  <div class="d-flex app-shell">
    <navigator :flag="navFlag" />

    <OnboardingWrapper class="playtest-wrapper">

      <!-- Step Indicator -->
      <div class="steps-card">
        <div class="steps-row">

          <!-- Invisible spacer mirrors help button for centering -->
          <button class="help-spacer" disabled aria-hidden="true"></button>

          <div class="steps-inner">
            <div class="step" v-for="(step, i) in steps" :key="i">
              <!-- Connector before each step except the first -->
              <div
                v-if="i > 0"
                class="connector"
                :class="playtestSectionIndex >= i ? 'connector-active' : 'connector-inactive'"
              ></div>

              <div class="step-body">
                <div
                  class="step-circle"
                  :class="playtestSectionIndex >= i ? 'circle-active' : 'circle-inactive'"
                >
                  <span v-if="playtestSectionIndex > i" class="checkmark">✓</span>
                  <span v-else>{{ i + 1 }}</span>
                </div>
                <span
                  class="step-label"
                  :class="playtestSectionIndex >= i ? 'label-active' : 'label-inactive'"
                >
                  {{ step }}
                </span>
              </div>
            </div>
          </div>

          <!-- Help button — triggers the new onboarding system -->
          <button class="help-btn" @click="triggerHelp()" aria-label="Help">?</button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="sections">
        <component
          v-if="loadComplete"
          :is="playtestComponent"
          @update_player_info="update_player_info"
          @update_player_items="update_player_items"
          @recording_finished="recording_finished"
          @screen_done="isNextDisabled = false"
          @timeline_item_dropped="onTimelineItemDropped"
          @disable_submit_on_new_recording="disableSubmitOnNewRecording"
        ></component>
        <div v-else class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading playtest data…</p>
        </div>
      </div>

      <!-- Navigation Bar -->
      <div class="nav-bar">
        <button class="nav-btn btn-secondary" :disabled="isDisabled" @click="previous_page()">
          ← Previous
        </button>
        <button class="nav-btn btn-save" @click="save_progress()">
          Save Progress
        </button>
        <button class="nav-btn btn-primary" :disabled="isNextDisabled" @click="next_page()">
          {{ nextText }} →
        </button>
      </div>

    </OnboardingWrapper>
  </div>
</template>

<script>
  import navigator from '../layout/NavBarNew.vue'
  import OnboardingWrapper from '../onboarding/OnboardingWrapper.vue'
  import PlaytestInfo from './Playtest-Info.vue'
  import PlaytestAudioVisualCheck from './Playtest-AudioVisualCheck.vue'
  import PlaytestMain from './Playtest-Main.vue'
  import PlayTesterUser from '../../Firestore/PlayTesterUser.js'
  import PlaytestScreenRecording from './Playtest-ScreenRecording.vue'
  import PermissionService from './Perm-Check.js'
  import GameTesting from '../../Firestore/GameTesting.js'
  import { onboardingService } from '@/services/OnboardingService'
  import { initializePageOnboarding } from '@/services/onboardingConfigs'
  import API_URL from '../../api/config.js'

  // Map each section index to its onboarding page name
  const ONBOARDING_PAGE_NAMES = ['playtestInfo', 'playtestAVCheck', 'playtestMain'];

  export default {
    name: "Playtest",
    components: {
      navigator,
      OnboardingWrapper,
      PlaytestInfo,
      PlaytestAudioVisualCheck,
      PlaytestMain,
      PlaytestScreenRecording,
    },
    data() {
      return {
        userType: window.localStorage.getItem("userType")
          ? parseInt(window.localStorage.getItem("userType")) : -1,
        navFlag: 0,
        gameName: window.localStorage.getItem("gameName"),
        isDisabled: true,
        isNextDisabled: false,
        playtestComponent: PlaytestInfo,
        playtestSectionIndex: 0,
        nextText: "Audio + Visual",
        steps: ["Select Template", "Audio + Video", "Start / End Playtest"],
        position: { top: 0, left: 0 },
        isRecording: false,
        videoRecorded: false,
        dataToSave: {},
        existingData: null,
        playtestInfo: null,
        loadComplete: false,
      }
    },
    mounted() {
      if (window.localStorage.getItem("userType")) {
        this.userType = parseInt(window.localStorage.getItem("userType"));
      } else {
        this.$router.push({ name: 'login' });
        return;
      }

      const playtestInfoStr = window.localStorage.getItem("playPeriodInfo");
      if (playtestInfoStr) {
        try { this.playtestInfo = JSON.parse(playtestInfoStr); }
        catch (e) { console.error("Error parsing playPeriodInfo:", e); this.playtestInfo = {}; }
      } else {
        this.playtestInfo = {};
      }

      var gameId       = window.localStorage.getItem('gameId');
      var testerId     = window.localStorage.getItem("userId");
      var playPeriodId = window.localStorage.getItem('playPeriodID');

      window.localStorage.setItem('savedPlayerInfo', null);
      window.localStorage.setItem("playtestSectionIndex", 0);

      GameTesting.loadTestSession(gameId, testerId, playPeriodId)
        .then(res  => { this.existingData = res.data; this.setup_page(); })
        .catch(err => { console.error(err); this.existingData = [{}]; this.setup_page(); });
    },
    methods: {
      // ── Navigation ──────────────────────────────────────────────────────────
      previous_page() {
        var idx = this.playtestSectionIndex;
        if (idx > 0) window.localStorage.setItem("playtestSectionIndex", idx - 1);
        this.loadComplete = false;
        this.$router.push({ name: 'playtest' });
        this.setup_page();
      },

      async next_page() {
        if (this.playtestSectionIndex === 1) {
          var ok = await PermissionService.hasMediaPermissions();
          if (!ok) { alert('Camera and microphone permissions are required to proceed.'); return; }
        }
        if (this.playtestSectionIndex === 2) {
          this.finish_playtest();
          this.$router.push({ name: 'dashboard' });
          return;
        }
        window.localStorage.setItem("playtestSectionIndex", this.playtestSectionIndex + 1);
        this.loadComplete = false;
        this.$router.push({ name: 'playtest' });
        this.setup_page();
      },

      // ── Page setup ──────────────────────────────────────────────────────────
      setup_page() {
        this.playtestSectionIndex = parseInt(window.localStorage.getItem("playtestSectionIndex"));

        var self = this;
        var testerId = window.localStorage.getItem("userId");

        // Auto-show onboarding for first-time users on each section
        PlayTesterUser.getPlayTesterProfile(testerId)
          .then(function(res) {
            var firstTime = res.data["firstTimeUser"][self.playtestSectionIndex + 1];
            if (firstTime === 1) {
              self.$nextTick(function() {
                initializePageOnboarding(
                  ONBOARDING_PAGE_NAMES[self.playtestSectionIndex],
                  self.userType,
                  onboardingService,
                  self.$store
                );
              });
              PlayTesterUser.updateTesterFirstTime(testerId, self.playtestSectionIndex + 1)
                .catch(function(e) { console.log(e); });
            }
          })
          .catch(function(e) { console.log(e); });

        if (this.playtestSectionIndex === 0) {
          if (this.existingData && this.existingData[0] && this.existingData[0].playtesterInfo)
            window.localStorage.setItem('savedPlayerInfo', JSON.stringify(this.existingData[0].playtesterInfo));
          this.playtestComponent = PlaytestInfo;
          this.isDisabled        = true;
          this.isNextDisabled    = false;
          this.nextText          = "Begin your playtest session";

        } else if (this.playtestSectionIndex === 1) {
          this.playtestComponent = PlaytestAudioVisualCheck;
          this.isDisabled        = false;
          this.isNextDisabled    = true;
          this.nextText          = "Start Gameplay Record";

        } else if (this.playtestSectionIndex === 2) {
          var d = this.existingData && this.existingData[0] ? this.existingData[0] : null;
          if (d && d.videoRecording)  window.localStorage.setItem('savedVideoRecording',  JSON.stringify(d.videoRecording));
          if (d && d.webcamRecording) window.localStorage.setItem('savedWebcamRecording', JSON.stringify(d.webcamRecording));
          if (d && d.instructions)    window.localStorage.setItem('savedInstructions',    JSON.stringify(d.instructions));
          if (d && d.bugs)            window.localStorage.setItem('savedBugs',            JSON.stringify(d.bugs));
          this.playtestComponent = PlaytestMain;
          this.isNextDisabled    = true;
          this.nextText          = "Submit Recording";
        }
        this.loadComplete = true;
      },

      onTimelineItemDropped() {
        this.timelineUsed = true;
        if (this.videoRecorded) {
          this.isNextDisabled = false;
        }
      },
      disableSubmitOnNewRecording(){
        this.timelineUsed = false;
        this.isNextDisabled = true;
      },

      // ── Help button — force-triggers onboarding for current section ─────────
      triggerHelp() {
        var self = this;
        this.$nextTick(function() {
          initializePageOnboarding(
            ONBOARDING_PAGE_NAMES[self.playtestSectionIndex],
            self.userType,
            onboardingService,
            self.$store,
            true // force = true so it always shows regardless of timer
          );
        });
      },

      // ── Recording & data events ─────────────────────────────────────────────
      recording_finished(videoUrl, duration, webcamUrl, transcript) {
        this.dataToSave['audioTranscript'] = transcript.split(';');
        this.dataToSave['videoRecording']  = {url: videoUrl, duration: duration};
        this.dataToSave['webcamRecording'] = webcamUrl;
        this.videoRecorded  = true;
        if (this.timelineUsed) {
          this.isNextDisabled = false;
        }
      },

      update_player_items(item, action) {
        if (action === 0) this.dataToSave['instructions'] = item;
        else              this.dataToSave['bugs']         = item;
      },

      update_player_info(answers) { this.dataToSave['playtesterInfo'] = answers; },

  _persistSave(status) {
    this.dataToSave['status'] = status;
    var gameId       = window.localStorage.getItem('gameId');
    var testerId     = window.localStorage.getItem("userId");
    var playPeriodId = window.localStorage.getItem('playPeriodID');

    GameTesting.loadTestSession(gameId, testerId, playPeriodId)
      .then((res) => {
        const existingSession = res.data && res.data[0] ? res.data[0] : {};

        // Merge existing fields with new data, then save
        const mergedData = {
          audioTranscript: existingSession.audioTranscript  || [],
          bugs:            existingSession.bugs             || [],
          videoRecording:  existingSession.videoRecording   || null,
          webcamRecording: existingSession.webcamRecording  || null,
          instructions:    existingSession.instructions     || [],
          playtesterInfo:  existingSession.playtesterInfo   || null,
          ...this.dataToSave,               // new data overwrites existing
          sessionID: existingSession.sessionID,  // always use the real Firestore doc ID
        };

        return GameTesting.savePlaytest(mergedData);
      })
    .then(() => console.log("Playtest saved successfully"))
    .catch((e) => console.error("Error saving playtest:", e));
    
    const name = "tester";
    const message = testerId;
    if(status === "Done"){
      fetch(`${API_URL}/email/devs/${gameId}/playtest-notif`, {
        method: 'POST',
        headers:{
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({name, message})
      }).then(response => response.json())
      .then(data => {
        console.log(data);
      }).catch((e) => console.error("Error sending email to dev", e));
    }
  },

      save_progress()   { this._persistSave("Active"); },
      finish_playtest() { this._persistSave("Done");   },
    }
  }
</script>

<style scoped>
/* ── Shell ── */
.app-shell {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.playtest-wrapper {
  flex: 1;
  min-width: 0;
  height: 100vh;
  background-color: #eeeeee;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  padding: 20px;
  gap: 16px;
}

/* ── Steps card ── */
.steps-card {
  background: white;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.steps-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.steps-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.step {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.step-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.connector {
  width: clamp(24px, 6vw, 90px);
  height: 3px;
  border-radius: 2px;
  margin-bottom: 24px;
  transition: background-color 0.3s ease;
  flex-shrink: 1;
}
.connector-active   { background-color: #ff7222; }
.connector-inactive { background-color: #d0d0d0; }

.step-circle {
  width:  clamp(36px, 3.5vw, 50px);
  height: clamp(36px, 3.5vw, 50px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(0.9rem, 1.4vw, 1.15rem);
  font-weight: 700;
  color: white;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}
.circle-active  {
  background: linear-gradient(135deg, #ff7222, #ff8e4d);
  box-shadow: 0 3px 8px rgba(255, 114, 34, 0.35);
}
.circle-inactive { background-color: #c8c8c8; }

.checkmark { font-size: 1rem; line-height: 1; }

.step-label {
  font-size: clamp(0.65rem, 1.1vw, 0.82rem);
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  transition: color 0.3s ease;
}
.label-active   { color: #ff7222; }
.label-inactive { color: #aaa; }

/* ── Help button ── */
.help-btn {
  width:  clamp(32px, 2.5vw, 42px);
  height: clamp(32px, 2.5vw, 42px);
  border-radius: 50%;
  border: 2px solid #ff7222;
  background: white;
  color: #ff7222;
  font-size: clamp(0.9rem, 1.3vw, 1.05rem);
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}
.help-btn:hover { background: #ff7222; color: white; }

.help-spacer {
  width:  clamp(32px, 2.5vw, 42px);
  height: clamp(32px, 2.5vw, 42px);
  border: none;
  background: transparent;
  visibility: hidden;
  flex-shrink: 0;
}

/* ── Content area ── */
.sections {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* Scrollbar styles inherited from global App.vue */

/* Loading state */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
  color: #888;
  font-size: 1rem;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e0e0e0;
  border-top-color: #ff7222;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Navigation bar ── */
.nav-bar {
  background: white;
  border-radius: 14px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-shrink: 0;
}

.nav-btn {
  border: none;
  border-radius: 8px;
  padding: 10px 22px;
  font-size: clamp(0.78rem, 1.1vw, 0.92rem);
  font-weight: bold;
  cursor: pointer;
  color: white;
  transition: transform 0.1s, box-shadow 0.2s, background 0.2s;
  white-space: nowrap;
  height: 44px;
}
.nav-btn:active { transform: scale(0.98); }

.btn-primary {
  background: linear-gradient(135deg, #ff7222, #ff8e4d);
  box-shadow: 0 3px 6px rgba(255, 114, 34, 0.3);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(255, 114, 34, 0.4);
}
.btn-primary:disabled {
  background: #d7cbc5;
  box-shadow: none;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f7f7f7;
  color: #333;
}
.btn-secondary:hover:not(:disabled) { background: #e9e9e9; }
.btn-secondary:disabled {
  background: #f0f0f0;
  color: #bbb;
  cursor: not-allowed;
}

.btn-save {
  background-color: #17a2b8;
  margin: 0 auto;
  box-shadow: 0 3px 6px rgba(23, 162, 184, 0.25);
}
.btn-save:hover {
  background-color: #138496;
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(23, 162, 184, 0.35);
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .playtest-wrapper { padding: 12px; gap: 12px; }
  .steps-card       { padding: 14px 16px; }
  .nav-bar          { padding: 10px 14px; }
  .connector        { width: clamp(14px, 3vw, 36px); }
  .nav-btn          { padding: 8px 14px; height: 40px; }
}

@media (max-width: 480px) {
  .step-label { display: none; }
  .connector  { margin-bottom: 0; }
  .nav-btn    { padding: 8px 10px; font-size: 0.75rem; }
  .nav-bar    { flex-wrap: wrap; }
  .btn-save   { order: 3; width: 100%; margin: 0; }
}
</style>


// WEBPACK FOOTER //
// src/components/playtest/Playtest.vue